/**
 * The patch. This file is the deliverable.
 *
 * It takes the REAL style — `buildStyle()` imported from `@geogame/globe`, not
 * a copy of it — and returns a new one with imagery underneath and a legibility
 * kit on top. Nothing here mutates the input, and `@geogame/globe` is never
 * touched. When the other workflow lands, `applyImageryPatch()` is the function
 * that gets folded into `style.ts`; everything else in this package is scaffold
 * for looking at the result.
 *
 * WHY IT CAN BE A PATCH AT ALL — the two facts the whole design rests on:
 *
 *  1. `style_layer.ts` `isHidden()` ignores opacity, and `query_features.ts`
 *     has no opacity check. So `countriesFill` at `fill-opacity: 0` is INVISIBLE
 *     AND FULLY PICKABLE. The imagery can therefore replace the land fill
 *     outright while `snap.ts`, `targets.ts`, `POLYGON_PICK_LAYERS` and
 *     `selectability.test.ts` carry on working on geometry that is still there.
 *     Picking is byte-for-byte unchanged by every skin in this lab. The lab
 *     asserts this at runtime — see `assertFillStillPickable` in `LabGlobe.tsx`.
 *
 *  2. Highlights are feature-state, so a highlight can be an OPACITY swap on
 *     the same layer rather than a colour swap. That matters: a colour swap
 *     reads on flat slate and disappears over terrain, because over imagery the
 *     eye has no reference for "this fill changed". A translucent overlay reads
 *     as a wash and is unambiguous over desert, ice, forest and sea.
 *
 * INVARIANT 7. Nothing here adds `glyphs`, `sprite`, a `symbol` layer, or any
 * URL other than a relative same-origin raster tile path. `labStyle.test`-style
 * assertions live in `invariants.ts` and run on every style build in the lab.
 */

import type { LayerSpecification, StyleSpecification } from 'maplibre-gl';
import { COLORS } from './theme';
import { LAYER, buildStyle, dotOpacity } from './style';
import { WAVE_IMAGE_ID } from './waves';

import type { Skin, Tuning } from './skins';

/*
 * MapLibre re-exports the layer union but not the individual property-value
 * spec types, and they are NOT interchangeable: `fill-opacity` is data-driven
 * (it can read a feature-state) while `raster-opacity` is not. Deriving each
 * one from the union keeps every expression below type-checked without
 * depending on @maplibre/maplibre-gl-style-spec and without a single `any`.
 */
type FillPaint = NonNullable<Extract<LayerSpecification, { type: 'fill' }>['paint']>;
type FillOpacity = NonNullable<FillPaint['fill-opacity']>;
type ColourValue = NonNullable<FillPaint['fill-color']>;
type LinePaint = NonNullable<Extract<LayerSpecification, { type: 'line' }>['paint']>;
type LineColour = NonNullable<LinePaint['line-color']>;
type LineWidth = NonNullable<LinePaint['line-width']>;
type LineOpacity = NonNullable<LinePaint['line-opacity']>;
type CirclePaint = NonNullable<Extract<LayerSpecification, { type: 'circle' }>['paint']>;
type CircleColour = NonNullable<CirclePaint['circle-color']>;
type CircleRadius = NonNullable<CirclePaint['circle-radius']>;
type RasterPaint = NonNullable<Extract<LayerSpecification, { type: 'raster' }>['paint']>;
type RasterOpacity = NonNullable<RasterPaint['raster-opacity']>;
type BackgroundPaint = NonNullable<Extract<LayerSpecification, { type: 'background' }>['paint']>;
type BackgroundOpacity = NonNullable<BackgroundPaint['background-opacity']>;

/**
 * The zoom window over which the imagery hands back to the flat vector globe.
 *
 * Shared by the raster's fade-out and the land fill's fade-IN, because those
 * two are the same handover seen from either side and drifting them apart is
 * how the globe ends up with a zoom band where neither is painting land.
 */
const FADE_START = 2.5;
const FADE_END = 5;

/** Layers this patch adds. Prefixed so they cannot collide with `LAYER`. */
export const LAB_LAYER = {
  earth: 'geogame-earth-raster',
  swell: 'geogame-ocean-swell',
  microShapeCasing: 'geogame-microshape-line-casing',
  borderCasing: 'geogame-countries-line-casing',
  highlightLine: 'geogame-countries-highlight-line',
  microUnder: 'geogame-micro-under',
} as const;

/**
 * EVERY LAYER THAT DRAWS A POLITICAL BORDER, base style and imagery patch
 * alike. The eclipse fades exactly this set and nothing else.
 *
 * It is a list here rather than three ids at the call site because the eclipse
 * shipped fading only the three in `LAYER`, and over imagery -- which is what
 * the game actually renders -- the line a player SEES is not those. It is the
 * casing underneath: near-black, 0.85 opacity, wider, added by this patch
 * precisely so a border survives being drawn over both Greenland ice and open
 * ocean. Fading the bright line and leaving the dark casing did not remove the
 * borders. It arguably sharpened them, against a globe that had just been
 * darkened to make them hard to read.
 *
 * `highlightLine` is deliberately ABSENT. It rests at opacity 0 and paints
 * only under a feature-state highlight, which is the round-end reveal, and the
 * reveal happens inside the eclipsed round. Fading it would mean an eclipsed
 * player never gets shown the answer, which is a different punishment from the
 * one this mechanic is.
 *
 * The graticule is absent for a related reason: it is not a border, and it is
 * what an eclipsed player has left to navigate by.
 */
export const BORDER_LINE_LAYERS: readonly string[] = [
  LAYER.countriesLine,
  LAYER.regionsLine,
  LAYER.microShapeLine,
  LAB_LAYER.borderCasing,
  LAB_LAYER.microShapeCasing,
];

export const EARTH_SOURCE = 'geogame-earth';

const HL = ['feature-state', 'hl'] as const;

/** `case` over the highlight feature-state with a per-kind value. */
function byHighlight<T>(correct: T, incorrect: T, neutral: T, none: T): unknown[] {
  return [
    'case',
    ['==', HL, 'correct'],
    correct,
    ['==', HL, 'incorrect'],
    incorrect,
    ['==', HL, 'neutral'],
    neutral,
    none,
  ];
}

/**
 * Declared tile size, which is NOT the image size.
 *
 * `covering_tiles.ts` computes `zt = floor(zoom + log2(512 / tileSize))` from
 * the DECLARED size but takes the texture from `img.width`. The images are
 * 512px (power of two, so `webgl/texture.ts` generates mipmaps and the limb
 * does not crawl during idle spin). Declaring 128 at dpr >= 1.5 selects level 2
 * at `HOME_VIEW.zoom = 0.9`, which lands the texture 1:1 with the ~304 CSS px
 * globe. Declaring 512 would select level 0 and look soft.
 */
export function declaredTileSize(dpr: number): number {
  return dpr >= 1.5 ? 128 : 256;
}

function rasterLayer(skin: Skin, tune: Tuning): LayerSpecification {
  return {
    id: LAB_LAYER.earth,
    type: 'raster',
    source: EARTH_SOURCE,
    paint: {
      /*
       * Ramp the imagery OUT above map zoom 2.5. Two reasons, and both are
       * about the round rather than about looks: zoom 5+ is the magnify /
       * disambiguate window where the player is doing precision work and wants
       * the flat maximally-legible globe back, and it is also where the level-4
       * tiles are being overzoomed and would start to look soft.
       */
      'raster-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        FADE_START,
        tune.opacityHome,
        FADE_END,
        tune.opacityDeep,
      ] as unknown as RasterOpacity,
      'raster-saturation': tune.saturation,
      'raster-contrast': tune.contrast,
      'raster-brightness-min': tune.brightnessMin,
      'raster-brightness-max': tune.brightnessMax,
      // Static build artefacts. There is nothing to cross-fade to.
      'raster-fade-duration': 0,
      'raster-resampling': 'linear',
    },
  };
}

/**
 * The animated ocean swell. See `waves.ts` for why it is a `background`
 * pattern and why it belongs UNDER the imagery.
 *
 * It fades out on the same zoom window the imagery does. Above zoom 2.5 the
 * player is doing precision work, and a moving texture under the thing they
 * are aiming at is exactly the wrong help.
 */
function swellLayer(opacity: number): LayerSpecification {
  return {
    id: LAB_LAYER.swell,
    type: 'background',
    paint: {
      'background-pattern': WAVE_IMAGE_ID,
      'background-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        2.5,
        opacity,
        4.5,
        0,
      ] as unknown as BackgroundOpacity,
    },
  };
}

function widthRamp(lo: number, hi: number): LineWidth {
  return ['interpolate', ['linear'], ['zoom'], 0, lo, 4, hi] as unknown as LineWidth;
}

/**
 * Apply imagery and the legibility kit to a style built by `@geogame/globe`.
 *
 * Pure. `base` is not mutated; the GeoJSON source objects are shared by
 * reference on purpose, so `map.setStyle(next, { diff: true })` sees them as
 * unchanged and never re-tiles 110m country polygons for a slider drag.
 */
export function applyImageryPatch(
  base: StyleSpecification,
  skin: Skin,
  tune: Tuning,
  dpr: number,
): StyleSpecification {
  const kit = skin.kit;
  const wantImagery = skin.tiles !== null && tune.opacityHome > 0;
  const wantKit = tune.kitEnabled;
  // The swell is only meaningful under imagery that leaves the sea translucent;
  // over an opaque skin it would be invisible, and over no skin at all it would
  // be a moving pattern on a flat vector globe.
  const wantSwell = wantImagery && tune.swellOpacity > 0;

  const sources: StyleSpecification['sources'] = { ...base.sources };
  if (wantImagery && skin.tiles) {
    sources[EARTH_SOURCE] = {
      type: 'raster',
      // RELATIVE. No scheme, no host. `request_manager.ts` passes it through
      // untouched, so this is same-origin by construction, and `LabGlobe`
      // installs a `transformRequest` that throws on any URL containing '://'.
      tiles: [skin.tiles],
      tileSize: declaredTileSize(dpr),
      minzoom: 0,
      // Above this MapLibre overzooms rather than fetching. That is what makes
      // the magnify window incapable of stalling on a tile request.
      maxzoom: 4,
      bounds: [-180, -85.0511287798066, 180, 85.0511287798066],
      attribution: '',
    };
  }

  const out: LayerSpecification[] = [];
  for (const layer of base.layers) {
    // Imagery goes directly above the ocean background and below everything
    // else. The vectors stay the source of truth; the imagery is decoration.
    if (layer.id === LAYER.ocean) {
      // A translucent-sea skin composites over this, so it has to be the
      // skin's own deep-water colour rather than the flat vector globe's.
      out.push(
        skin.oceanBase && layer.type === 'background'
          ? { ...layer, paint: { ...layer.paint, 'background-color': skin.oceanBase } }
          : layer,
      );
      if (wantSwell) out.push(swellLayer(tune.swellOpacity));
      if (wantImagery) out.push(rasterLayer(skin, tune));
      continue;
    }

    /*
     * The land fill is handled OUTSIDE the kit toggle, because hiding it is not
     * a legibility choice — it is the thing that makes imagery visible at all.
     * An opaque `COLORS.land` fill covers every pixel of the raster, so a "kit
     * off" that left it alone would show no imagery and prove nothing.
     *
     * What the toggle changes here is the HIGHLIGHT:
     *   kit on  — a translucent wash (`tune.highlightOpacity`), so the terrain
     *             still reads through and the eye can see which country it is;
     *   kit off — the naive opaque colour swap, which is what the shipped style
     *             does on flat slate and which blots the imagery out entirely.
     *
     * `fill-opacity: 0` at rest keeps the layer in `POLYGON_PICK_LAYERS` and
     * fully queryable — `isHidden()` ignores opacity — so not one line of
     * picking changes either way. `landVeilOpacity` above 0 puts a wash of
     * `COLORS.land` back over the terrain: the cheapest available knob for
     * knocking the Sahara down without re-baking anything.
     */
    if (wantImagery && layer.id === LAYER.countriesFill && layer.type === 'fill') {
      const hl = wantKit ? tune.highlightOpacity : 1;
      /*
       * DEEP ZOOM. Above `FADE_END` the imagery is down to `opacityDeep` and
       * this fill is the only thing left that can paint a continent, so it
       * comes up as the photograph goes down. Without the ramp, zooming in on
       * Malta or Sao Tome faded the island out from under its own outline and
       * left a shape drawn on open water.
       *
       * Both stops are tied to `opacityDeep` rather than fixed, so a skin that
       * declines the fade (`terra`, at 1) gets a flat no-op ramp and keeps its
       * colour, while a skin that fades to nothing gets the full flat-globe
       * treatment back — and neither needs a second constant kept in sync by
       * hand.
       *
       * The highlight rides the same lever. Over full imagery a translucent
       * wash is right, because the terrain has to read through it; over a flat
       * globe there is no terrain to protect and the shipped behaviour is a
       * near-solid colour.
       */
      const gone = 1 - tune.opacityDeep;
      const hlDeep = wantKit ? hl + (1 - hl) * gone : 1;
      const veil = wantKit ? tune.landVeilOpacity : 0;
      const veilDeep = wantKit ? veil + (kit.landVeilDeep - veil) * gone : 0;
      out.push({
        ...layer,
        paint: {
          ...layer.paint,
          // Zoom outermost, feature-state within: a zoom-and-property function,
          // which is the only nesting order MapLibre accepts.
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            FADE_START,
            byHighlight(hl, hl, wantKit ? hl * 0.8 : 1, veil),
            FADE_END,
            byHighlight(hlDeep, hlDeep, wantKit ? hlDeep * 0.8 : 1, veilDeep),
          ] as unknown as FillOpacity,
        },
      });
      continue;
    }

    if (!wantKit) {
      out.push(layer);
      continue;
    }

    if (layer.id === LAYER.graticule && layer.type === 'line') {
      out.push({
        ...layer,
        paint: {
          ...layer.paint,
          'line-color': kit.graticule,
          'line-opacity': tune.graticuleOpacity,
        },
      });
      continue;
    }

    if (layer.id === LAYER.countriesLine && layer.type === 'line') {
      const m = tune.borderWidthMult;
      /*
       * THE BORDER PAIR. Casing first, wide and at one end of the luminance
       * range; line second, narrow and at the other. Satellite imagery spans
       * the whole range — Greenland ice measures 186, open ocean 25 — so no
       * single colour survives it, and this is the only treatment that does.
       */
      out.push({
        id: LAB_LAYER.borderCasing,
        type: 'line',
        source: layer.source,
        paint: {
          'line-color': kit.borderCasing,
          'line-width': widthRamp(0.5 * m + kit.casingWidthPx, 1.2 * m + kit.casingWidthPx),
          'line-opacity': kit.borderCasingOpacity,
          'line-blur': 0.5,
        },
      });
      out.push({
        ...layer,
        paint: {
          ...layer.paint,
          'line-color': byHighlight(
            COLORS.correct,
            COLORS.incorrect,
            COLORS.neutral,
            kit.borderLine,
          ) as unknown as LineColour,
          'line-width': widthRamp(0.5 * m, 1.2 * m),
          'line-opacity': 0.95,
        },
      });
      /*
       * A highlighted country also gets a bright rim. The translucent fill
       * alone is legible but soft-edged over terrain; the rim is what makes
       * "this exact country" unambiguous when the neighbour is the same colour
       * of desert.
       */
      out.push({
        id: LAB_LAYER.highlightLine,
        type: 'line',
        source: layer.source,
        paint: {
          'line-color': byHighlight(
            COLORS.correct,
            COLORS.incorrect,
            COLORS.neutral,
            COLORS.correct,
          ) as unknown as LineColour,
          'line-width': widthRamp(2.2 * m, 3.4 * m),
          'line-opacity': byHighlight(0.95, 0.95, 0.85, 0) as unknown as LineOpacity,
          'line-blur': 0.3,
        },
      });
      continue;
    }

    /*
     * THE REAL ISLAND. Over imagery these countries are no longer a dot on open
     * water — they are a shape, and it has to read as land rather than as a
     * translucent sticker.
     *
     * The fill is opaque and takes the kit's island colour, because unlike a
     * continent there is nothing underneath it worth showing through: the bake
     * rescued these islands from a photograph that was too coarse to hold them,
     * so what is beneath is an approximation of the same island. A highlight
     * still swaps the colour outright — over a shape this small a translucent
     * wash would be the difference between two blue-greens on a blue sea.
     */
    if (wantKit && layer.id === LAYER.microShapeFill && layer.type === 'fill') {
      out.push({
        ...layer,
        paint: {
          ...layer.paint,
          'fill-color': byHighlight(
            COLORS.correct,
            COLORS.incorrect,
            COLORS.neutral,
            kit.microFill,
          ) as unknown as ColourValue,
          'fill-opacity': 0.95,
        },
      });
      continue;
    }

    if (wantKit && layer.id === LAYER.microShapeLine && layer.type === 'line') {
      const m = kit.borderWidthMult;
      // The same casing/line pair the country borders get, for the same reason:
      // no single colour survives both a bright reef and open ocean.
      out.push({
        id: LAB_LAYER.microShapeCasing,
        type: 'line',
        source: layer.source,
        paint: {
          'line-color': kit.borderCasing,
          'line-width': widthRamp(0.5 * m + kit.casingWidthPx, 1.2 * m + kit.casingWidthPx),
          'line-opacity': kit.borderCasingOpacity,
          'line-blur': 0.5,
        },
      });
      out.push({
        ...layer,
        paint: {
          ...layer.paint,
          'line-color': byHighlight(
            COLORS.correct,
            COLORS.incorrect,
            COLORS.neutral,
            kit.borderLine,
          ) as unknown as LineColour,
          'line-width': widthRamp(0.5 * m, 1.2 * m),
          'line-opacity': 0.95,
        },
      });
      continue;
    }

    if (layer.id === LAYER.microDot && layer.type === 'circle') {
      /*
       * MICROSTATE DOTS. ~29 countries have no 110m polygon at all and are
       * pickable ONLY as these dots. A 2.6px dot in `COLORS.microstate`
       * (#3a5069) is a land-coloured speck: on flat slate that reads as a tiny
       * island, which is the intent, but over Blue Marble it is camouflage.
       *
       * The fix is an under-disc rather than a brighter dot. A brighter dot
       * competes with the border pair and starts to read as a marker; a dark
       * (or, for a bright-land skin, light) disc beneath it guarantees a
       * luminance step at the dot's edge whatever the terrain is doing, and
       * leaves the dot itself alone.
       */
      const r0 = kit.microRadiusPx;
      const r5 = kit.microRadiusPx * 2.4;
      out.push({
        id: LAB_LAYER.microUnder,
        type: 'circle',
        source: layer.source,
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            r0 + kit.microUnderBoostPx,
            5,
            r5 + kit.microUnderBoostPx,
          ] as unknown as CircleRadius,
          'circle-color': kit.microUnder,
          // Retires with the dot it sits under. A halo left behind by a faded
          // dot is a dark smudge on the sea next to a perfectly good island.
          'circle-opacity': dotOpacity(kit.microUnderOpacity),
          'circle-blur': 0.25,
        },
      });
      out.push({
        ...layer,
        paint: {
          ...layer.paint,
          'circle-color': byHighlight(
            COLORS.correct,
            COLORS.incorrect,
            COLORS.neutral,
            kit.microFill,
          ) as unknown as CircleColour,
          'circle-stroke-color': kit.microEdge,
          'circle-stroke-width': kit.microEdgeWidth,
          'circle-stroke-opacity': dotOpacity(1),
        },
      });
      continue;
    }

    if (layer.id === LAYER.pinCore && layer.type === 'circle') {
      // Pins keep their colours; only the stroke widens, so a "you" pin cannot
      // be lost against a bright coastline or a city-lights cluster.
      out.push({
        ...layer,
        paint: {
          ...layer.paint,
          'circle-stroke-color': kit.pinStroke,
          'circle-stroke-width': 2.2,
        },
      });
      continue;
    }

    if (layer.id === LAYER.markCore && layer.type === 'circle') {
      out.push({
        ...layer,
        paint: { ...layer.paint, 'circle-stroke-color': kit.pinStroke, 'circle-stroke-width': 1.6 },
      });
      continue;
    }

    out.push(layer);
  }

  return { ...base, sources, layers: out };
}

/** Convenience: build the real style, then patch it. */
export function buildLabStyle(skin: Skin, tune: Tuning, dpr: number): StyleSpecification {
  const base = buildStyle({ graticule: tune.graticule });
  return applyImageryPatch(base, skin, tune, dpr);
}
